import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setAuth0Logout, setTokenGetter } from './token-holder';
import { startIdleMonitor } from './idle-monitor';
import { registerWebSocketCloser } from './force-logout';
import { connectSocket, disconnectSocket } from '@/lib/ws/socket';

// Wires the Auth0 SDK getters into module-level holders so non-React code
// (ky interceptor, idle monitor, force-logout, Socket.IO auth callback) can
// call them. Per docs/ui/auth0-frontend-tds.md §6.3 + §13, mount this once
// inside Auth0Provider.
//
// Operator-ui idle threshold is 30 min per docs/ui/operator-ui-tds.md §7 —
// looser than admin-ui (15 min) because operators work multi-hour shifts.
//
// Also owns the Socket.IO singleton lifecycle (NEX-147 + TDS §5.1): connect
// when authenticated, register disconnectSocket as the WS-close hook for
// force-logout step 1, tear the socket down on sign-out.

export function AuthBridge(): null {
  const { getAccessTokenSilently, logout, isAuthenticated } = useAuth0();

  useEffect(() => {
    setTokenGetter(getAccessTokenSilently);
    setAuth0Logout(logout);
    return () => {
      setTokenGetter(null);
      setAuth0Logout(null);
    };
  }, [getAccessTokenSilently, logout]);

  useEffect(() => {
    if (!isAuthenticated) return;
    return startIdleMonitor({ idleMs: 30 * 60 * 1000 });
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // VITE_WS_DISABLE=true skips the Socket.IO singleton entirely. Useful in
    // dev when the BFF (NEX-69 redis-ui Pub/Sub, NEX-72 /cases namespace)
    // hasn't shipped the WS surface yet — without this gate the client
    // reconnects forever, ReconnectionBanner pops up, and the demo looks
    // broken even though every UI feature works fine. Hard-coded to DEV
    // builds so production never silently skips real-time updates.
    if (import.meta.env.VITE_WS_DISABLE === 'true' && import.meta.env.DEV) {
      return;
    }
    try {
      connectSocket();
      registerWebSocketCloser(disconnectSocket);
    } catch (err) {
      // VITE_WS_BASE_URL not set — surface to console but don't block render.
      // Mock provider dev flow still works without a live BFF socket.
      console.warn('[auth-bridge] Socket.IO not connected:', err);
    }
    return () => {
      registerWebSocketCloser(null);
      void disconnectSocket();
    };
  }, [isAuthenticated]);

  return null;
}
